import { AlertTriangle, ArrowDown, ArrowUp, CalendarClock, CheckCircle2, ChevronsUp, Circle, CircleDashed, CircleDot, Eye, GitMerge, Globe, HelpCircle, Lock, Minus, Rocket, XCircle } from 'lucide-react';
import type { DeliveryState, Priority, RequestStatus, Visibility, WorkPhase } from '@vwt/contracts';
import { Badge, cn, Tooltip } from '@vwt/ui';
import { useTranslation } from '../lib/i18n.js';
import { formatCalendarDate, todayInDisplayZone } from '../lib/format.js';

/** Status and priority always pair color with text and an icon (Section 14.1). */
const phaseTone: Record<WorkPhase, 'neutral' | 'info' | 'warning' | 'accent' | 'success' | 'outline'> = {
  TODO: 'neutral',
  IN_PROGRESS: 'info',
  IN_REVIEW: 'warning',
  INTEGRATED: 'accent',
  DONE: 'success',
  CANCELED: 'outline',
};
const phaseIcon: Record<WorkPhase, typeof Circle> = {
  TODO: Circle,
  IN_PROGRESS: CircleDot,
  IN_REVIEW: Eye,
  INTEGRATED: GitMerge,
  DONE: CheckCircle2,
  CANCELED: XCircle,
};

export function PhaseBadge({ phase }: { phase: WorkPhase }) {
  const { t } = useTranslation();
  const Icon = phaseIcon[phase];
  return (
    <Badge tone={phaseTone[phase]}>
      <Icon aria-hidden />
      {t(`status.phase.${phase}`)}
    </Badge>
  );
}

const requestTone: Record<RequestStatus, 'neutral' | 'info' | 'warning' | 'accent' | 'success' | 'danger' | 'outline'> = {
  DRAFT: 'outline',
  SUBMITTED: 'info',
  NEEDS_INFORMATION: 'warning',
  ACCEPTED: 'accent',
  REJECTED: 'neutral',
  DUPLICATE: 'neutral',
  RESOLVED: 'success',
  NEEDS_ATTENTION: 'danger',
  CANCELED: 'outline',
};

export function RequestStatusBadge({ status }: { status: RequestStatus }) {
  const { t } = useTranslation();
  const Icon = status === 'RESOLVED' ? CheckCircle2 : status === 'NEEDS_ATTENTION' ? AlertTriangle : status === 'DRAFT' ? CircleDashed : status === 'NEEDS_INFORMATION' ? HelpCircle : Circle;
  return (
    <Badge tone={requestTone[status]}>
      <Icon aria-hidden />
      {t(`status.request.${status}`)}
    </Badge>
  );
}

const priorityIcon: Record<Priority, typeof Circle> = { URGENT: ChevronsUp, HIGH: ArrowUp, NORMAL: Minus, LOW: ArrowDown };
const priorityColor: Record<Priority, string> = { URGENT: 'text-danger', HIGH: 'text-warning', NORMAL: 'text-fg-subtle', LOW: 'text-fg-subtle' };

export function PriorityLabel({ priority, compact = false }: { priority: Priority; compact?: boolean }) {
  const { t } = useTranslation();
  const Icon = priorityIcon[priority];
  const label = t(`status.priority.${priority}`);
  const content = (
    <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
      <Icon className={`size-3.5 ${priorityColor[priority]}`} aria-hidden />
      {compact ? <span className="sr-only">{label}</span> : label}
    </span>
  );
  return compact ? <Tooltip content={label}>{content}</Tooltip> : content;
}

const deliveryTone: Record<DeliveryState, 'neutral' | 'warning' | 'success' | 'danger' | 'outline'> = {
  UNKNOWN: 'outline',
  NOT_DEPLOYED: 'neutral',
  PARTIAL: 'warning',
  DEPLOYED: 'success',
  REVERTED: 'danger',
};

export function DeliveryBadge({ state }: { state: DeliveryState }) {
  const { t } = useTranslation();
  return (
    <Badge tone={deliveryTone[state]}>
      <Rocket aria-hidden />
      {t(`status.delivery.${state}`)}
    </Badge>
  );
}

export function VisibilityBadge({ visibility }: { visibility: Visibility }) {
  const { t } = useTranslation();
  return visibility === 'RESTRICTED' ? (
    <Tooltip content={t('status.visibilityHelp.RESTRICTED')}>
      <span>
        <Badge tone="warning">
          <Lock aria-hidden />
          {t('status.visibility.RESTRICTED')}
        </Badge>
      </span>
    </Tooltip>
  ) : (
    <Tooltip content={t('status.visibilityHelp.COMPANY')}>
      <span>
        <Badge tone="neutral">
          <Globe aria-hidden />
          {t('status.visibility.COMPANY')}
        </Badge>
      </span>
    </Tooltip>
  );
}

/** Sub-item progress: done over total with a small bar (canceled sub-items are not counted). */
export function ProgressPill({ progress, className }: { progress: { total: number; done: number }; className?: string }) {
  const { t } = useTranslation();
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const label = t('work.detail.progressOf', { done: progress.done, total: progress.total });
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1.5 text-xs text-fg-muted tabular-nums', className)} title={label}>
      <span className="h-1.5 w-12 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.done} aria-label={label}>
        <span className={cn('block h-full rounded-full', pct === 100 ? 'bg-success' : 'bg-accent')} style={{ width: `${pct}%` }} />
      </span>
      {progress.done}/{progress.total}
    </span>
  );
}

/** Due date with state: overdue (open work past due) in red, due today in amber. */
export function DueLabel({ date, phase, compact = false }: { date: string | null; phase: WorkPhase; compact?: boolean }) {
  const { t } = useTranslation();
  if (!date) return null;
  const open = phase !== 'DONE' && phase !== 'CANCELED';
  const today = todayInDisplayZone();
  const state = open && date < today ? 'overdue' : open && date === today ? 'today' : 'normal';
  const label = formatCalendarDate(date, compact ? { month: 'short', day: 'numeric' } : { dateStyle: 'medium' });
  const hint = state === 'overdue' ? t('work.detail.overdue') : state === 'today' ? t('work.detail.dueToday') : t('work.detail.dueDate');
  return (
    <span title={`${hint}: ${formatCalendarDate(date)}`} className={cn('inline-flex items-center gap-1 text-xs whitespace-nowrap', state === 'overdue' ? 'font-medium text-danger' : state === 'today' ? 'font-medium text-warning' : 'text-fg-muted')}>
      <CalendarClock className="size-3.5" aria-hidden />
      {label}
      {state !== 'normal' && !compact ? <span className="sr-only">({hint})</span> : null}
    </span>
  );
}

export function KeyLabel({ value }: { value: string }) {
  return <span className="font-mono text-xs text-fg-subtle tabular-nums">{value}</span>;
}
